import React, { useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useGetCurrentUser } from '../store'
import { AuthContext } from '../context/auth-context'
import { ConnectionStatus, IOTConnectionHandler, ResultPair } from './iot-connection-handler'
import { handleIotMessage } from './iot-event-handler'
import { CustomAuthoriserData } from '../services/service-utils'

const region = import.meta.env.VITE_AWS_REGION as string
const websocketEndPoint = import.meta.env.VITE_WEBSOCKET_API_ENDPOINT as string

export type ConnectIoTContextType = {
  iotConnected: boolean
  connectionStatus: ConnectionStatus
  setGuestCredentials: (authData: CustomAuthoriserData | undefined) => void
  subscribeToChannel: (topicName: string, handleMessage: (topicName: string, payload: string) => void) => Promise<ResultPair>
  unsubscribeFromChannel: (topicName: string) => void
  sendMessage: (topicName: string, payload: string) => void
}

export const ConnectIoTContext = React.createContext<ConnectIoTContextType>({
  iotConnected: false,
  connectionStatus: ConnectionStatus.Disconnected,
  setGuestCredentials: () => { },
  subscribeToChannel: async () => { return { statusCode: 500, message: 'No connection to subscribe to' } },
  unsubscribeFromChannel: () => { },
  sendMessage: () => { },
})

const ConnectIoT: React.FC<React.PropsWithChildren> = ({ children }) => {

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.Stopped)
  const [iotConnected, setIotConnected] = useState<boolean>(false)
  const [authData, setAuthData] = useState<CustomAuthoriserData | undefined>(undefined)

  const queryClient = useQueryClient()

  const { data: currentUser = null, isLoading } = useGetCurrentUser()
  const { tryGetStoredSession, availableClientIds } = React.useContext(AuthContext)

  const handleDefaultMessage = (topicName: string, payload: string, userId: string) => {
    handleIotMessage(topicName, payload, userId, queryClient)
  }

  const [connectionHandler] =
    useState<IOTConnectionHandler>(new IOTConnectionHandler(
      region || "",
      websocketEndPoint || "",
      tryGetStoredSession,
      handleDefaultMessage,
    ))

  const onConnectionStatusChange = (status: ConnectionStatus) => {
    setConnectionStatus(status)
    setIotConnected(status === ConnectionStatus.Connected)
  }

  useEffect(() => {
    if (isLoading) {
      return
    }

    connectionHandler.checkUserChange(currentUser, availableClientIds, authData)
  }, [currentUser, availableClientIds, authData, connectionHandler, isLoading])

  useEffect(() => {
    connectionHandler.onConnectionStatusChange(onConnectionStatusChange)

    return () => {
      console.log("Unsubscribing to iot")
      connectionHandler.closeConnection('Disconnecting from AWS IoT due to unmount')
      connectionHandler.removeConnectionStatusChangeListener(onConnectionStatusChange)
    }
  }, [connectionHandler])

  const subscribeToChannel = async (topicName: string, handleMessage: (topicName: string, payload: string) => void): Promise<ResultPair> => {
    return connectionHandler.subscribeToChannel(topicName, handleMessage)
  }

  const sendMessage = (topicName: string, payload: string) => {
    return connectionHandler.sendMessage(topicName, payload)
  }

  const unsubscribeFromChannel = async (topicName: string) => {
    return connectionHandler.unsubscribeFromChannel(topicName)
  }

  const setGuestCredentials = (authData: CustomAuthoriserData | undefined) => {
    setAuthData(authData)
  }

  return (
    <ConnectIoTContext.Provider value={{
      setGuestCredentials,
      subscribeToChannel,
      unsubscribeFromChannel,
      sendMessage,
      iotConnected,
      connectionStatus,
    }}>
      {children}
    </ConnectIoTContext.Provider>
  )
}

export default ConnectIoT